import React, { Fragment, useRef, useState, useEffect } from 'react';
import classname from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import AppStorage from '@Services/AppStorage';
import QueryletHeader from '../Querylet/QueryletHeader';
import QueryletSearchField from '../Querylet/QueryletSearchField';
import OperatorSelection from '../OperatorSelection/OperatorSelection';
import { addToBuilderAction, mergeNodeItemAction, onPropUnSelectAction, ungroupAction, updateOperatorAction } from '../QueryletActions';
import '../QueryContainer/QueryContainer.scss';
import { OPERATORS_CACHE_KEY } from '../QueryPanel/hooks/useFetchOperator';
import { BOUND_OPS_CACHE_KEY } from '../QueryPanel/hooks/useFetchBoundOps';
import './NodeRenderer.scss';

function NodeRenderer({ index, node, nodesList, boundOps, operators, itemIndex, type, onToggleQuerylet }) {
  const ref = useRef(null);
  const [operatorList, setOperatorList] = useState(AppStorage.instance.get(OPERATORS_CACHE_KEY) || []);
  const [boundOpsList, setBoundOpsList] = useState(AppStorage.instance.get(BOUND_OPS_CACHE_KEY) || {});

  useEffect(() => {
    if (operators) {
      setOperatorList(operators);
    }
  }, [operators]);

  useEffect(() => {
    if (boundOps) {
      setBoundOpsList(boundOps);
    }
  }, [boundOps]);

  const dispatch = useDispatch();
  const [collectedDragProps, dragRef] = useDrag({
    type: 'query-item',
    item: () => ({ index, id: node.id, key: node.uuid, itemIndex, obj: node }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  function subItemAddtoBuilder(dragIndex, collectedProps) {
    if (dragIndex.type === 'DragNode') {
      const indexValue = dragIndex.node && dragIndex.node.type === 'form' ? dragIndex.node.displayName : dragIndex.node.id;
      dispatch(
        addToBuilderAction(dragIndex.node, 'default', {
          dragId: indexValue,
          dropId: collectedProps.uuid,
          issubItem: true,
          isForm: dragIndex.node.type === 'form',
        })
      );
    } else {
      dispatch(
        mergeNodeItemAction(dragIndex, nodesList, {
          type: 'subItem',
          key: collectedProps.uuid,
        })
      );
    }
  }

  function nodeAddtoBuilder(dragIndex, collectedProps) {
    const isForm = dragIndex.node && dragIndex.node.type === 'form';
    /** Drag directly in node from side nav */
    if (dragIndex.type === 'DragNode') {
      const indexValue = dragIndex.node.type === 'form' ? dragIndex.node.displayName : dragIndex.node.id;
      dispatch(
        addToBuilderAction(dragIndex.node, 'default', {
          dragId: indexValue,
          dropId: collectedProps.uuid,
          isForm,
        })
      );
    } else {
      dispatch(
        mergeNodeItemAction(dragIndex, nodesList, {
          type: 'subItem',
          key: collectedProps.uuid,
        })
      );
    }
  }

  const [collectedProps, dropRef] = useDrop({
    accept: ['query-item', 'DragNode'],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverShallow: monitor.isOver({ shallow: true }),
      current: ref.current,
      uuid: ref.current && ref.current.getAttribute('data-uuid'),
    }),
    drop: (dragIndex, monitor) => {
      if (
        monitor.didDrop() ||
        collectedProps.uuid === dragIndex.key ||
        (dragIndex.obj &&
          dragIndex.obj.isShell &&
          dragIndex.obj.items &&
          dragIndex.obj.items.findIndex((item) => item.uuid === collectedProps.uuid) > -1)
      )
        return;
      if (type === 'subItem') {
        subItemAddtoBuilder(dragIndex, collectedProps);
      } else if (type === 'Node' && collectedProps.isOver) {
        nodeAddtoBuilder(dragIndex, collectedProps);
      }
    },
  });

  const dragDropRef = dragRef(dropRef(ref));
  const deleteQuery = 'Delete Querylet';

  const handleOperatorChange = (operatorS) => {
    // eslint-disable-next-line no-shadow
    const { operator, qualifier, node } = operatorS;
    dispatch(updateOperatorAction({ operator, node, qualifier }));
  };

  return (
    <div key={`parent-${node.displayName}`} role="region" aria-label={`query group called ${node.displayName}`}>
      {index > 0 && type !== 'subItem' && (
        <OperatorSelection node={node} key={`node-${node.displayName}`} updateOperator={handleOperatorChange} operators={operatorList} />
      )}
      <div
        className={classname('ql__prop', {
          'ql__prop--grouped-queries': itemIndex !== undefined,
          'ql__prop--sub-item': nodesList.items && nodesList.items.length > 0,
          'ql__prop--with-wrapper': node.id === 'wrapper' && node.type !== 'history',
          'ql__prop--with-many-fields': !node.canExist && node.childrenCount > 1,
          'ql__prop--with-drag': collectedProps.isOverShallow,
          'u-els-margin-top-1x': node.trackingSearchType && node.trackingSearchType[0] === 'forms',
        })}>
        <div
          className={classname('ql__prop__container', {
            'ql__prop--drag': collectedDragProps.isDragging,
            ql__prop__container__openquerylet: node.openQuerylet,
            'ql__prop__container--structure': node.type === 'structure',
          })}
          key={`parent-${index}-${node.uuid}`}
          role="region"
          aria-label={`query group called ${node.groupName ? node.groupName : node.displayName}`}>
          <div
            className={classname('ql__prop__container', {
              'u-els-width-1o1': node.id === 'wrapper',
            })}
            ref={dragDropRef}
            key={`node-${index}-${node.uuid}`}
            data-uuid={node.uuid}>
            <Flyout trigger="hover" flyout="Drag" placement="bottom" theme="simple">
              <ButtonWithIcon
                id={`querybuilder-dragfield-${node.id}`}
                draggable
                className="cursor-move"
                onClick={(dragE) => dragE.preventDefault()}
                linkIconSize="xxs"
                iconOnly
                type="borderless"
                sprite="Drag"
                tabIndex="-1">
                drag
              </ButtonWithIcon>
            </Flyout>
            <QueryletHeader
              node={node}
              parent={nodesList}
              isDragging={collectedDragProps.isDragging}
              onToggleQuerylet={onToggleQuerylet}
              isNumeric={node.canExist}
              isGrouped={node.items && node.items.length > 0}
            />
            {node.items && node.items.length > 0 && (
              <div className="ql__prop__header--group--actions">
                {node.groupName && !node.isImmutable && (
                  <Flyout trigger="hover" flyout="Ungroup fields" placement="bottom" theme="simple">
                    <ButtonWithIcon
                      iconRight
                      layout="alt"
                      key="Ungroup"
                      id="groupedQuery"
                      type="link"
                      sprite="Link"
                      onClick={() => dispatch(ungroupAction(nodesList, node.uuid))}>
                      Ungroup
                    </ButtonWithIcon>
                  </Flyout>
                )}
                <Flyout trigger="hover" flyout="Delete group" placement="bottom" theme="simple">
                  <ButtonWithIcon
                    iconRight
                    layout="alt"
                    className="cursor-pointer"
                    id="querybuilder-deletequerylet-button"
                    key="closeQuerylet"
                    type="link"
                    sprite="Trash"
                    onClick={() => dispatch(onPropUnSelectAction(node.uuid, nodesList))}>
                    Delete
                  </ButtonWithIcon>
                </Flyout>
              </div>
            )}
          </div>
          {(!node.canExist || node.openQuerylet) && <QueryletSearchField subItem={node} boundOps={boundOpsList} index={index} />}
          {((node.type === 'advanced' && node.id !== 'wrapper') ||
            (node.type === 'structure' && node.id !== 'wrapper') ||
            (node.type === 'structure' && node.id !== 'wrapper') ||
            node.type === 'history' ||
            (node.trackingSearchType === 'fields' && !node.canExist && node.children?.length > 0)) && (
            <Flyout trigger="hover" flyout="Delete Querylet" placement="bottom" theme="simple">
              <ButtonWithIcon
                id="closeQuerylet"
                sprite="Trash"
                className="cursor-pointer"
                iconOnly
                type="link"
                onClick={() => dispatch(onPropUnSelectAction(node.uuid, nodesList))}>
                Delete Querylet
              </ButtonWithIcon>
            </Flyout>
          )}
        </div>
        {node.items &&
          node.items.length > 0 &&
          node.items.map((subItem, cIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`subItem-${cIndex}-${node.uuid}`}>
              {cIndex > 0 && <OperatorSelection node={subItem} updateOperator={handleOperatorChange} operators={operatorList} />}
              <div className="ql__container">
                <NodeRenderer
                  index={index}
                  itemIndex={cIndex}
                  type="subItem"
                  boundOps={boundOpsList}
                  node={subItem}
                  nodesList={nodesList}
                  onToggleQuerylet={onToggleQuerylet}
                />
              </div>
            </Fragment>
          ))}
      </div>
    </div>
  );
}

NodeRenderer.propTypes = {
  node: PropTypes.object,
  index: PropTypes.number,
  itemIndex: PropTypes.number,
  boundOps: PropTypes.object,
  type: PropTypes.string,
  nodesList: PropTypes.array,
  operators: PropTypes.array,
  onToggleQuerylet: PropTypes.func,
};

export default NodeRenderer;
